import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { SignaturePad } from "@/components/SignaturePad";
import { ArrowLeft, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

export default function FormSubmissionFill() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [values, setValues] = useState({});
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get(`/form-submissions/${submissionId}`)
      .then((res) => {
        setSubmission(res.data);
        setValues(res.data.field_values || {});
      })
      .catch(() => toast.error("Could not load form"))
      .finally(() => setLoading(false));
  }, [submissionId]);

  const updateValue = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const submit = async () => {
    if (submission.form?.requires_signature && !signature) {
      toast.error("Please sign the form before submitting");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/form-submissions/${submissionId}/submit`, {
        field_values: values,
        signature_data_url: signature,
      });
      toast.success("Form submitted and signed successfully!");
      navigate("/forms");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit form");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !submission) return <Skeleton className="h-96 rounded-[var(--radius)]" />;

  const isCompleted = submission.status === "completed";

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="form-fill-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="form-fill-back-button">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div className="rounded-[var(--radius)] border bg-card p-5 sm:p-8 space-y-6">
        <div>
          <h1 className="font-heading text-xl font-semibold">{submission.form_title}</h1>
          {submission.form?.description && <p className="text-sm text-muted-foreground mt-1">{submission.form.description}</p>}
        </div>

        {isCompleted ? (
          <div className="text-center py-8 space-y-3">
            <FileCheck2 className="h-10 w-10 mx-auto text-emerald-600" />
            <p className="font-medium">This form has already been completed and signed.</p>
            {submission.signed_pdf_url && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}${submission.signed_pdf_url}`} target="_blank" rel="noreferrer" className="text-[hsl(var(--role-accent))] font-medium" data-testid="view-signed-pdf-link">
                View Signed PDF
              </a>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {(submission.form?.fields || []).map((field) => (
                <div key={field.key} className="space-y-1.5">
                  <Label>{field.label}</Label>
                  {field.type === "textarea" && (
                    <Textarea value={values[field.key] || ""} onChange={(e) => updateValue(field.key, e.target.value)} data-testid={`form-field-${field.key}`} />
                  )}
                  {field.type === "date" && (
                    <Input type="date" value={values[field.key] || ""} onChange={(e) => updateValue(field.key, e.target.value)} data-testid={`form-field-${field.key}`} />
                  )}
                  {field.type === "checkbox" && (
                    <div className="flex items-center gap-2">
                      <Checkbox checked={!!values[field.key]} onCheckedChange={(v) => updateValue(field.key, v)} data-testid={`form-field-${field.key}`} />
                      <span className="text-sm text-muted-foreground">I agree / confirm</span>
                    </div>
                  )}
                  {(!field.type || field.type === "text") && (
                    <Input value={values[field.key] || ""} onChange={(e) => updateValue(field.key, e.target.value)} data-testid={`form-field-${field.key}`} />
                  )}
                </div>
              ))}
            </div>

            {submission.form?.requires_signature !== false && (
              <div className="space-y-2 border-t pt-5">
                <Label>Your signature</Label>
                <SignaturePad onSave={setSignature} savedUrl={signature} />
              </div>
            )}

            <Button className="w-full h-11 bg-[hsl(var(--role-accent))] hover:opacity-95" onClick={submit} disabled={submitting} data-testid="submit-signed-form-button">
              Submit & Sign
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
